
/**
 * Removed all the props from T with the same keys from K
 * Source: https://stackoverflow.com/a/50918777
 */
export type Without<T, K> = Omit<T, keyof K>;

/**
 * Apply Partial to all keys on Interface except for the keys in ToKeepRequired.
 *
 * e.g.
 * interface A {
 *     foo: boolean;
 *     bar: boolean;
 *     baz: string;
 *     qux: number;
 * }
 *
 * type B = PartialRequired<A, "foo" | "qux">
 *
 * B is now {
 *     foo: boolean;
 *     bar?: boolean;
 *     baz?: string;
 *     qux: number;
 * }
 */
export type PartialRequired<Interface, ToKeepRequired extends keyof Interface> = Partial<
    Omit<Interface, ToKeepRequired>
> & Pick<Interface, ToKeepRequired>;

/**
 * Give a union type of all the possible value types from an object
 */
export type ValueOf<T> = T[keyof T];

/**
 * Removes the readonly modifier from object T
 */
export type Writeable<T> = { -readonly [P in keyof T]: T[P] };

/**
 * Recursively removes readonly modifier from object T
 */
export type DeepWriteable<T> = { -readonly [P in keyof T]: DeepWriteable<T[P]> };

/**
 * Infers the element type of an array
 */
export type ArrayElement<A> = A extends ReadonlyArray<infer T> ? T : never;

/**
 * Override a type with the specified properties
 * (probably best to look at OrderEdit.tsx for an example).
 *
 * e.g.
 * interface A {
 *     foo: number;
 *     bar: number;
 * }
 *
 * type B = Override<A, {
 *     foo: string;
 * }>
 *
 * B is now {
 *     foo: string;
 *     bar: number;
 * }
 */
export type Override<Original, ToOverride> = Omit<Original, keyof ToOverride> & ToOverride;

/**
 * Type guard to narrow key to keyof type
 * @param object
 * @param key
 */
export function isKeyOf<T extends Record<string, any>>(object: T, key: string | number | symbol): key is keyof T {
    return Object.prototype.hasOwnProperty.call(object, key);
}

/**
 * Unwrap a promise's internal type, useful for when using ReturnType.
 *
 * e.g.
 * const a: Promise<string> = Promise.resolve("a");
 * const b: UnwrapPromise<typeof a> = "a";
 *
 * // or
 *
 * const salePromise = CancellablePromise<ReturnType<typeof loadSalesHistory>>;
 *
 * Note: CancellablePromise now uses this so a promise can be passed to it and it will be automatically unwrapped.
 */
export type UnwrapPromise<P> = P extends Promise<infer U> ? U : P;

/**
 * A property which might be a promise or the promise's wrapped type
 */
export type MaybePromise<P> = P | Promise<P>;

/**
 * Type which matches any constructor with any arguments
 */
export type AnyConstructor = new (...args: Array<any>) => {};

/**
 * A class constructor for the provided instance type
 */
export type ConstructorClass<T> = new (...args: Array<any>) => T;

/**
 * A class constructor that takes the correct arguments
 */
export type ConstructorClassWithArgs<T> = new (...args: ConstructorParameters<ConstructorClass<T>>) => T;

/**
 * Convert a string from snake_case to camelCase
 */
export type CamelCase<S extends string> = S extends `${infer P1}_${infer P2}${infer P3}` ?
    `${Lowercase<P1>}${Uppercase<P2>}${CamelCase<P3>}` :
    Lowercase<S>;

/**
 * Type guard for unknown record object
 * @param data
 */
export function isRecord(data: unknown): data is Record<keyof any, unknown> {
    if(typeof data !== "object") {
        return false;
    }

    return data !== null;
}

/**
 * Ensure that a value is never called
 * @param type
 */
export const assertNever = (type: never): never => {
    throw new Error(`Invalid option provided: ${type}`);
};
